import React, { useState, useEffect } from 'react';
import { AccommodationInfo } from './types';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { PlacesAutocompleteV1 } from '@/components/ui/places-autocomplete-v1';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { 
  ImagePlus, 
  Wand2, 
  Building, 
  Trash, 
  ChevronDown, 
  ChevronRight,
  Calendar,
  Clock,
  MapPin,
  DollarSign,
  Tag,
  Bed,
  Home,
  FileText,
  Image,
  AlignLeft,
  CircleDot,
  ClipboardList,
  PencilLine,
  Bookmark,
  MoreHorizontal,
  Copy,
  RefreshCw,
  ArrowRight
} from 'lucide-react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { useOpenAiApi } from '@/hooks/useOpenAiApi';
import { useToast } from '@/hooks/use-toast';
import { SaveToLibraryModal } from './SaveToLibraryModal';
import { isItemSavedInLibrary } from '@/lib/library-utils';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';

// Custom styled label with orange underline and icon
const StyledLabel = ({ 
  children, 
  htmlFor, 
  className, 
  icon: Icon = CircleDot,
  ...props 
}: React.ComponentProps<typeof Label> & { icon?: React.ElementType }) => (
  <Label 
    htmlFor={htmlFor}
    className={cn("border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left w-fit text-sm font-medium text-gray-700", className)}
    {...props}
  >
    <Icon className="h-3.5 w-3.5 text-voah-coral flex-shrink-0" />
    <span className="whitespace-nowrap">{children}</span>
  </Label>
);

// Update input styles by creating a component wrapper
const FormInput = ({ className, ...props }: React.ComponentProps<typeof Input>) => (
  <Input
    className={cn("text-sm text-gray-600 font-normal", className)}
    {...props}
  />
);

const FormTextarea = ({ className, ...props }: React.ComponentProps<typeof Textarea>) => (
  <Textarea
    className={cn("text-sm text-gray-600 font-normal", className)}
    {...props}
  />
);

interface AccommodationFormProps {
  accommodation: AccommodationInfo;
  index: number;
  pageId: string;
  updateAccommodation: (pageId: string, accommodationId: string, field: keyof AccommodationInfo, value: any) => void;
  batchUpdateAccommodation?: (accommodationId: string, updates: Partial<AccommodationInfo>) => void;
  deleteAccommodation: (pageId: string, accommodationId: string) => void;
  duplicateAccommodation?: (pageId: string, accommodationId: string) => void;
  moveAccommodationToPage?: (accommodationId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function AccommodationForm({
  accommodation,
  index,
  pageId,
  updateAccommodation,
  batchUpdateAccommodation,
  deleteAccommodation,
  duplicateAccommodation,
  moveAccommodationToPage,
  isCollapsed,
  onToggleCollapse
}: AccommodationFormProps) {
  const { toast } = useToast();
  const { chatCompletion } = useOpenAiApi();
  const [isImageManagerOpen, setIsImageManagerOpen] = useState(false);
  const [isProcessingDescription, setIsProcessingDescription] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSaveToLibraryOpen, setIsSaveToLibraryOpen] = useState(false);
  const [isSavedToLibrary, setIsSavedToLibrary] = useState(false);
  const [shouldShowDeleteConfirm, setShouldShowDeleteConfirm] = useState(false);
  const [accommodationTitle, setAccommodationTitle] = useState<string>(() => {
    // Initialize title based on current accommodation
    if (accommodation.name) {
      return accommodation.name;
    } else {
      return `Hospedagem #${index + 1}`;
    }
  });
  // Track if location differs from title for sync icon
  const [locationDiffersFromTitle, setLocationDiffersFromTitle] = useState(false);

  // Effect to keep accommodationTitle in sync with accommodation.name
  useEffect(() => {
    setAccommodationTitle(accommodation.name || `Hospedagem #${index + 1}`);
  }, [accommodation.name, index]);

  // Effect to check if location differs from title
  useEffect(() => {
    setLocationDiffersFromTitle(
      !!accommodation.name && 
      (accommodation.location === '' || 
       accommodation.location === undefined || 
       accommodation.name !== accommodation.location)
    );
  }, [accommodation.name, accommodation.location]);

  // Debug effect to track accommodation changes
  useEffect(() => {
    // Limitar os logs para não sobrecarregar o console
    // apenas logar quando houver mudanças significativas
    if (accommodation.location || accommodation.coordinates) {
      const addressPreview = accommodation.address 
        ? accommodation.address.substring(0, 20) + (accommodation.address.length > 20 ? '...' : '')
        : 'N/A';
        
      console.log("[AccommodationForm] Accommodation updated:", {
        id: accommodation.id,
        address: addressPreview,
        hasLocation: !!accommodation.location,
        hasCoordinates: !!accommodation.coordinates
      });
    }
  }, [accommodation.id, accommodation.address, accommodation.location, accommodation.coordinates]);

  // Verificar se o item já está salvo na biblioteca - com menos frequência
  useEffect(() => {
    let isMounted = true;
    let timeoutId: number;
    
    // Utilizar um debounce para não verificar a cada pequena mudança
    const checkLibraryStatus = () => {
      if (timeoutId) clearTimeout(timeoutId);
      
      timeoutId = window.setTimeout(async () => {
        try {
          const isSaved = await isItemSavedInLibrary(accommodation, 'accommodation');
          if (isMounted) {
            setIsSavedToLibrary(isSaved);
          }
        } catch (error) {
          console.error('Erro ao verificar status na biblioteca:', error);
        }
      }, 2000); // Verificar após 2 segundos sem mudanças
    };
    
    checkLibraryStatus();
    
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [accommodation.id, accommodation.name, accommodation.address]); // Menos dependências

  // Function to update a field
  const handleUpdate = (field: keyof AccommodationInfo, value: any) => {
    let safeValue = value;
    
    // Special handling for coordinates to ensure they're properly formatted
    if (field === 'coordinates' && value) {
      const lat = typeof value.lat === 'string' ? parseFloat(value.lat) : Number(value.lat);
      const lng = typeof value.lng === 'string' ? parseFloat(value.lng) : Number(value.lng);
      
      safeValue = {
        lat: isNaN(lat) ? 0 : lat,
        lng: isNaN(lng) ? 0 : lng
      };
    }
    
    // Add special log for address field updates
    if (field === 'address') {
      console.log("[AccommodationForm] Updating address field:", {
        oldValue: accommodation.address,
        newValue: value,
        valueType: typeof value
      });
    }
    
    updateAccommodation(pageId, accommodation.id, field, safeValue);
  };

  // AI Description enhancement
  const handleProcessDescription = async () => {
    if (!accommodation.description || accommodation.description.trim() === '') {
      toast({
        title: "Descrição vazia",
        description: "Por favor, insira um texto na descrição para ser processado pela IA.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessingDescription(true);
    
    try {
      // Extract text from HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = accommodation.description;
      const textContent = tempDiv.textContent || tempDiv.innerText || '';

      // Enviar apenas o texto do usuário, sem prompts adicionais
      // Increase maxTokens to avoid truncation
      const response = await chatCompletion(textContent, 'gpt-4-turbo', 0.7, 1500);
      
      if (response && response.choices && response.choices.length > 0) {
        // Extrair o conteúdo da mensagem da resposta
        const generatedContent = response.choices[0].message.content.trim();
        
        // Check if the response was truncated
        if (response.choices[0].finish_reason === 'length') {
          toast({
            title: "Aviso de truncamento",
            description: "O texto gerado foi truncado devido ao limite de tamanho. Considere dividir o conteúdo em partes menores.",
            variant: "default"
          });
        }
        
        // Atualizar o campo de descrição com o conteúdo extraído
        handleUpdate('description', generatedContent);
        
        toast({
          title: "Descrição processada",
          description: "A descrição foi melhorada com sucesso pela IA.",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Erro ao processar descrição:', error);
      toast({
        title: "Erro",
        description: "Houve um erro ao processar a descrição. Por favor, tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsProcessingDescription(false);
    }
  };

  // Format currency values
  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Parse currency input
  const parseCurrency = (value: string): number | null => {
    if (!value) return null;
    const numericValue = value.replace(/\D/g, '');
    return numericValue ? Number(numericValue) / 100 : null;
  };

  // Handler para o botão de salvar na biblioteca
  const handleSaveToLibrary = (e: React.MouseEvent) => {
    e.stopPropagation(); // Impedir que o clique colapse/expanda o item
    setIsSaveToLibraryOpen(true);
  };

  // Finalizar edição do título
  const handleFinishTitleEdit = () => {
    setIsEditingTitle(false);
    
    // Se o título não estiver vazio
    if (accommodationTitle.trim() !== '') {
      handleUpdate('name', accommodationTitle.trim());
    } else {
      // Se estiver vazio, defina o campo name como vazio
      handleUpdate('name', '');
    }
  };

  // Function to sync location with title
  const handleSyncLocationWithTitle = () => {
    if (accommodation.name) {
      handleUpdate('location', accommodation.name);
    }
  };

  return (
    <>
      <Card className={cn(
        "border shadow-sm transition-all border-l-4 border-l-green-500",
        isCollapsed && "border-transparent border-l-green-500"
      )}>
        <CardHeader 
          className={cn(
            "cursor-pointer flex items-center justify-between",
            isCollapsed ? "py-2 px-3" : "py-3 px-4"
          )}
          onClick={onToggleCollapse}
        >
          <div className="flex items-center gap-2 w-full">
            <Building className="h-4 w-4 text-green-500" />
            
            {isEditingTitle ? (
              <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={accommodationTitle}
                  onChange={(e) => setAccommodationTitle(e.target.value)}
                  className="h-7 mr-2 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-transparent"
                  autoFocus
                  onFocus={() => {
                    // Se for apenas o placeholder, limpe o campo para edição
                    if (accommodationTitle.match(/^Hospedagem #\d+$/)) {
                      setAccommodationTitle('');
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleFinishTitleEdit();
                    }
                  }}
                  onBlur={handleFinishTitleEdit}
                />
              </div>
            ) : (
              <>
                {isCollapsed ? (
                  <span className="font-medium">{accommodationTitle}</span>
                ) : (
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditingTitle(true);
                    }}
                  >
                    <h3 className="font-medium">{accommodationTitle}</h3>
                  </div>
                )}
              </>
            )}
            
            {/* Quick info for collapsed view */}
            {isCollapsed && (
              <>
                {accommodation.address && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <MapPin className="h-3.5 w-3.5 mr-1" />
                    <span>{accommodation.address.substring(0, 20)}{accommodation.address.length > 20 ? '...' : ''}</span>
                  </div>
                )}
                
                {accommodation.roomType && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <Bed className="h-3.5 w-3.5 mr-1" />
                    <span>{accommodation.roomType}</span>
                  </div>
                )}
                
                {(accommodation.checkInDate || accommodation.checkOutDate) && (
                  <div className="flex items-center ml-3 text-sm text-gray-500">
                    <Clock className="h-3.5 w-3.5 mr-1" />
                    <span>
                      {accommodation.checkInDate && `${accommodation.checkInDate.slice(5).replace('-', '/')}`}
                      {accommodation.checkInDate && accommodation.checkOutDate && ' - '}
                      {accommodation.checkOutDate && `${accommodation.checkOutDate.slice(5).replace('-', '/')}`}
                    </span>
                  </div>
                )}
              </>
            )}
            
            <div className="flex items-center ml-auto">
              {/* Botão de Salvar na Biblioteca (bookmark) */}
              <Button
                variant={isSavedToLibrary ? "default" : "ghost"}
                size="icon"
                className={cn(
                  "h-8 w-8 transition-colors",
                  isSavedToLibrary 
                    ? "bg-amber-500 hover:bg-amber-600" 
                    : "hover:text-amber-500"
                )}
                onClick={handleSaveToLibrary}
                title={isSavedToLibrary ? "Item salvo na biblioteca" : "Salvar na Biblioteca"}
              >
                <Bookmark className={cn(
                  "h-4 w-4", 
                  isSavedToLibrary 
                    ? "text-white" 
                    : "text-gray-500 hover:text-amber-500"
                )} />
              </Button>
              
              {/* Dropdown menu with actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4 text-gray-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[180px]">
                  {duplicateAccommodation && (
                    <DropdownMenuItem 
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateAccommodation(pageId, accommodation.id);
                      }}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      <span>Duplicar</span>
                    </DropdownMenuItem>
                  )}
                  
                  {moveAccommodationToPage && (
                    <DropdownMenuItem 
                      onClick={(e) => {
                        e.stopPropagation();
                        moveAccommodationToPage(accommodation.id);
                      }}
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />
                      <span>Mover</span>
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteAccommodation(pageId, accommodation.id);
                    }}
                    className="text-red-600"
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    <span>Excluir</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {isCollapsed ? (
                <ChevronRight className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </div>
          </div>
        </CardHeader>

        {!isCollapsed && (
          <CardContent className="p-4 pt-0 space-y-4">
            {/* Name and Location fields side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 flex flex-col">
                <StyledLabel 
                  htmlFor={`name-${accommodation.id}`}
                  icon={Tag}
                >
                  Nome da Hospedagem
                </StyledLabel>
                <FormInput
                  id={`name-${accommodation.id}`}
                  value={accommodationTitle}
                  onChange={(e) => setAccommodationTitle(e.target.value)}
                  onFocus={() => {
                    // Se for apenas o placeholder, limpe o campo para edição
                    if (accommodationTitle.match(/^Hospedagem #\d+$/)) {
                      setAccommodationTitle('');
                    }
                  }}
                  onBlur={handleFinishTitleEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleFinishTitleEdit();
                    }
                  }}
                  placeholder="Nome do hotel ou hospedagem"
                />
              </div>
            
            {/* Location with Google Places integration */}
              <div className="space-y-2 flex flex-col">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    htmlFor={`location-${accommodation.id}`}
                    icon={MapPin}
                  >
                    Local
                  </StyledLabel>
                </div>
                <div className="relative">
                  <PlacesAutocompleteV1
                    value={accommodation.location || ''}
                    onChange={(value) => handleUpdate('location', value)}
                    onPlaceSelect={(place) => {
                      if (place) {
                        console.log("[AccommodationForm] Place selected:", {
                          place,
                          placeIdType: typeof place.placeId,
                          addressType: typeof place.address,
                          addressValue: place.address,
                          addressFieldBefore: accommodation.address
                        });
                        
                        // Make sure we're updating with a valid string
                        const addressToUse = place.address || '';
                        
                        // Create an updates object with all location-related fields
                        const updates: Partial<AccommodationInfo> = {
                          placeId: place.placeId,
                          googleMapsUrl: place.googleMapsUrl,
                          address: addressToUse,
                          coordinates: place.coordinates ? {
                            lat: Number(place.coordinates.lat),
                            lng: Number(place.coordinates.lng)
                          } : undefined,
                          city: place.city,
                          country: place.country
                        };
                        
                        console.log("[AccommodationForm] Normalized coordinates:", {
                          original: place.coordinates,
                          normalized: updates.coordinates,
                          types: {
                            originalLatType: place.coordinates ? typeof place.coordinates.lat : 'undefined',
                            originalLngType: place.coordinates ? typeof place.coordinates.lng : 'undefined',
                            normalizedLatType: updates.coordinates ? typeof updates.coordinates.lat : 'undefined',
                            normalizedLngType: updates.coordinates ? typeof updates.coordinates.lng : 'undefined'
                          }
                        });
                        
                        // Use batch update if available
                        if (batchUpdateAccommodation) {
                          batchUpdateAccommodation(accommodation.id, updates);
                        } else {
                          // Fallback to individual updates
                          Object.entries(updates).forEach(([key, value]) => {
                            handleUpdate(key as keyof AccommodationInfo, value);
                          });
                        }
                      }
                    }}
                    placeholder="Digite o local da hospedagem"
                  />
                  {locationDiffersFromTitle && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-10 top-0 h-10 w-10"
                      onClick={handleSyncLocationWithTitle}
                      title="Sincronizar localização com o título"
                    >
                      <RefreshCw className="h-4 w-4 text-gray-500" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Check-in and Check-out fields side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 flex flex-col">
                <StyledLabel 
                  htmlFor={`checkin-date-${accommodation.id}`}
                  icon={Calendar}
                >
                  Data de Check-in
                </StyledLabel>
                <FormInput
                  id={`checkin-date-${accommodation.id}`}
                  type="date"
                  value={accommodation.checkInDate || ''}
                  onChange={(e) => handleUpdate('checkInDate', e.target.value)}
                />
              </div>
              
              <div className="space-y-2 flex flex-col">
                <StyledLabel 
                  htmlFor={`checkout-date-${accommodation.id}`}
                  icon={Calendar}
                >
                  Data de Check-out
                </StyledLabel>
                <FormInput
                  id={`checkout-date-${accommodation.id}`}
                  type="date"
                  value={accommodation.checkOutDate || ''}
                  onChange={(e) => handleUpdate('checkOutDate', e.target.value)}
                />
              </div>
            </div>
            
            {/* Check-in and Check-out time fields side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 flex flex-col">
                <StyledLabel 
                  htmlFor={`checkin-time-${accommodation.id}`}
                  icon={Clock}
                >
                  Horário Check-in
                </StyledLabel>
                <FormInput
                  type="time"
                  id={`checkin-time-${accommodation.id}`}
                  value={accommodation.checkInTime || ''}
                  onChange={(e) => handleUpdate('checkInTime', e.target.value)}
                />
              </div>
              
              <div className="space-y-2 flex flex-col">
                <StyledLabel 
                  htmlFor={`checkout-time-${accommodation.id}`}
                  icon={Clock}
                >
                  Horário Check-out
                </StyledLabel>
                <FormInput
                  type="time" 
                  id={`checkout-time-${accommodation.id}`}
                  value={accommodation.checkOutTime || ''}
                  onChange={(e) => handleUpdate('checkOutTime', e.target.value)}
                />
              </div>
            </div>
            
            {/* Address */}
            <div className="space-y-2 flex flex-col">
              <div className="flex items-center justify-between w-full">
                <StyledLabel 
                  htmlFor={`address-${accommodation.id}`}
                  icon={Home}
                >
                  Endereço
                </StyledLabel>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`show-address-${accommodation.id}`}
                    checked={accommodation.showAddress === true}
                    onCheckedChange={(checked) => 
                      handleUpdate('showAddress', checked === true)
                    }
                  />
                  <Label 
                    htmlFor={`show-address-${accommodation.id}`}
                    className="text-sm font-normal cursor-pointer text-gray-600"
                  >
                    Mostrar na cotação
                  </Label>
                </div>
              </div>
              <FormInput
                id={`address-${accommodation.id}`}
                value={accommodation.address || ''}
                onChange={(e) => handleUpdate('address', e.target.value)}
                placeholder="Endereço completo da hospedagem"
              />
            </div>
            
            {/* Description with rich text editor */}
            <div className="space-y-2 flex flex-col">
              <div className="flex items-center justify-between">
                <StyledLabel 
                  htmlFor={`description-${accommodation.id}`}
                  icon={AlignLeft}
                >
                  Descrição
                </StyledLabel>
              </div>
              <RichTextEditor
                value={accommodation.description || ''}
                onChange={(value) => handleUpdate('description', value)}
                placeholder="Descreva a hospedagem..."
                onProcessWithAI={handleProcessDescription}
                isProcessing={isProcessingDescription}
              />
            </div>
            
            {/* Image management */}
            <div className="pt-4 border-t space-y-2">
              <div className="flex justify-between items-center">
                <div className="flex flex-col items-start">
                  <StyledLabel
                    className="text-sm font-medium"
                    icon={Image}
                  >
                    Imagens
                  </StyledLabel>
                </div>
                <div className="flex items-center gap-2">
                  {accommodation.images && accommodation.images.length > 0 ? (
                    <div className="flex items-center gap-1">
                      <div 
                        className="h-8 w-8 rounded-md overflow-hidden border border-gray-200 shadow-sm hover:border-[#087a83] cursor-pointer transition-all hover:scale-105"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsImageManagerOpen(true);
                        }}
                      >
                        <img 
                          src={accommodation.images[0]} 
                          alt="Miniatura" 
                          className="h-full w-full object-cover"
                        />
                      </div>
                      {accommodation.images.length > 1 && (
                        <span 
                          className="text-xs text-gray-500 cursor-pointer hover:text-[#087a83]"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsImageManagerOpen(true);
                          }}
                        >
                          +{accommodation.images.length - 1}
                        </span>
                      )}
                    </div>
                  ) : null}
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsImageManagerOpen(true);
                    }}
                    className="h-8 bg-white border-gray-200 hover:bg-gray-50 text-[#087a83]"
                  >
                    <ImagePlus className="h-4 w-4 mr-2" />
                    <span>Gerenciar imagens</span>
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Price section at bottom */}
            <div className="mt-4 pt-4 border-t">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 flex flex-col">
                  <StyledLabel 
                    htmlFor={`price-${accommodation.id}`}
                    icon={DollarSign}
                  >
                    Preço
                  </StyledLabel>
                  <div className="relative">
                    <DollarSign className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-500" />
                    <FormInput
                      id={`price-${accommodation.id}`}
                      value={accommodation.price ? formatCurrency(accommodation.price) : ''}
                      onChange={(e) => {
                        const parsed = parseCurrency(e.target.value);
                        handleUpdate('price', parsed);
                      }}
                      className="pl-8"
                      placeholder="0,00"
                    />
                  </div>
                </div>
                <div className="space-y-2 flex flex-col">
                  <StyledLabel 
                    htmlFor={`currency-${accommodation.id}`}
                    icon={Tag}
                  >
                    Moeda
                  </StyledLabel>
                  <FormInput
                    id={`currency-${accommodation.id}`}
                    value={accommodation.currency || 'BRL'}
                    onChange={(e) => handleUpdate('currency', e.target.value)}
                    placeholder="BRL"
                  />
                </div>
                <div className="space-y-2 flex flex-col">
                  <StyledLabel 
                    htmlFor={`roomtype-${accommodation.id}`}
                    icon={Bed}
                  >
                    Tipo de Quarto
                  </StyledLabel>
                  <FormInput
                    id={`roomtype-${accommodation.id}`}
                    value={accommodation.roomType || ''}
                    onChange={(e) => handleUpdate('roomType', e.target.value)}
                    placeholder="Ex: Quarto Duplo Standard"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2 flex flex-col">
                  <StyledLabel 
                    htmlFor={`price-notes-${accommodation.id}`}
                    icon={FileText}
                  >
                    Observações sobre o preço
                  </StyledLabel>
                  <FormTextarea
                  id={`price-notes-${accommodation.id}`}
                  value={accommodation.priceNotes || ''}
                  onChange={(e) => handleUpdate('priceNotes', e.target.value)}
                    placeholder="Por quarto, por noite, por pessoa, etc..."
                    rows={1}
                />
              </div>
                <div className="space-y-2 flex flex-col">
                  <StyledLabel 
                    htmlFor={`notes-${accommodation.id}`}
                    icon={ClipboardList}
                  >
                    Tipo de Pensão
                  </StyledLabel>
                  <FormTextarea
                id={`notes-${accommodation.id}`}
                value={accommodation.notes || ''}
                onChange={(e) => handleUpdate('notes', e.target.value)}
                    placeholder="Café da manhã, meia pensão, pensão completa, etc..."
                    rows={1}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
      
      {/* Modal para Salvar na Biblioteca */}
      {isSaveToLibraryOpen && (
        <SaveToLibraryModal
          isOpen={isSaveToLibraryOpen}
          onClose={() => setIsSaveToLibraryOpen(false)}
          item={accommodation}
          itemType="accommodation"
          onSave={(savedItem) => {
            setIsSavedToLibrary(true);
            toast({
              title: "Item salvo na biblioteca",
              description: "A hospedagem foi salva com sucesso na sua biblioteca.",
            });
            setIsSaveToLibraryOpen(false);
          }}
        />
      )}
      
      {isImageManagerOpen && (
        <ImageManagerModal
          isOpen={isImageManagerOpen}
          onClose={() => setIsImageManagerOpen(false)}
          images={accommodation.images || []}
          onImagesChange={(images) => {
            handleUpdate('images', images);
          }}
          contextId={accommodation.id}
          contextType="accommodation"
          bookingStayParams={{
            checkin: accommodation.checkInDate,
            checkout: accommodation.checkOutDate,
          }}
        />
      )}
    </>
  );
} 